import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  HomeIcon,
  UserGroupIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  KeyIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  UserIcon,
  ShieldCheckIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import {
  userService,
  showPermissionAlert,
} from "../services/permissionService";

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    role: "admin_team",
    permissions: [],
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      console.log("Loading users and permissions...");
      console.log(
        "Current user:",
        JSON.parse(localStorage.getItem("user") || "{}")
      );
      console.log("Current token:", localStorage.getItem("token"));

      const [usersResponse, permissionsResponse] = await Promise.all([
        userService.getUsers(),
        userService.getPermissions(),
      ]);

      console.log("Users response:", usersResponse);
      console.log("Permissions response:", permissionsResponse);

      setUsers(usersResponse.data.data);
      setPermissions(permissionsResponse.data.data);
    } catch (error) {
      console.error("Error loading data:", error);
      console.error("Error details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });
      // Show permission alert for explicit access attempt
      if (!showPermissionAlert(error, "view user management")) {
        alert(
          `Error loading data: ${
            error.response?.data?.message || error.message
          }. Please try again.`
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await userService.updateUser(editingUser.id, formData);
      } else {
        await userService.createUser(formData);
      }

      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error("Error saving user:", error);
      // Show permission alert for explicit save action
      if (
        !showPermissionAlert(
          error,
          editingUser ? "update users" : "create users"
        )
      ) {
        alert(
          error.response?.data?.message ||
            "Error saving user. Please try again."
        );
      }
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await userService.deleteUser(userId);
        loadData();
      } catch (error) {
        console.error("Error deleting user:", error);
        // Show permission alert for explicit delete action
        if (!showPermissionAlert(error, "delete users")) {
          alert(
            error.response?.data?.message ||
              "Error deleting user. Please try again."
          );
        }
      }
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: "",
      role: user.role,
      permissions: user.permissions || [],
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      username: "",
      password: "",
      role: "admin_team",
      permissions: [],
    });
  };

  const toggleUserExpansion = (userId) => {
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedUsers(newExpanded);
  };

  const handlePermissionToggle = (permission) => {
    const updatedPermissions = formData.permissions.includes(permission)
      ? formData.permissions.filter((p) => p !== permission)
      : [...formData.permissions, permission];

    setFormData({
      ...formData,
      permissions: updatedPermissions,
    });
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "admin":
        return <ShieldCheckIcon className="h-5 w-5 text-red-600" />;
      case "admin_team":
        return <UsersIcon className="h-5 w-5 text-blue-600" />;
      default:
        return <UserIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-800";
      case "admin_team":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Navigation Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/dashboard"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <HomeIcon className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Link>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            <PlusIcon className="h-5 w-5" />
            Add User
          </button>
        </div>

        <div className="mt-4">
          <div className="flex items-center gap-3 mb-2">
            <UserGroupIcon className="h-8 w-8 text-gray-700" />
            <h1 className="text-3xl font-bold text-gray-900">
              User Management
            </h1>
          </div>
          <p className="text-gray-600">
            Manage system users and their permissions. Create new accounts and
            assign specific access levels.
          </p>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900">System Users</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Permissions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((user, index) => (
                <React.Fragment key={user.id}>
                  <tr
                    className="hover:bg-gray-50"
                    style={{ display: index === 0 ? "none" : "table-row" }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <button
                          onClick={() => toggleUserExpansion(user.id)}
                          className="mr-2 text-gray-400 hover:text-gray-600"
                        >
                          {expandedUsers.has(user.id) ? (
                            <ChevronDownIcon className="h-4 w-4" />
                          ) : (
                            <ChevronRightIcon className="h-4 w-4" />
                          )}
                        </button>
                        <div className="flex items-center">
                          {getRoleIcon(user.role)}
                          <div className="ml-3">
                            <div
                              className="text-sm font-medium text-gray-900"
                              style={{
                                display: index === 0 ? "none" : "block",
                              }}
                            >
                              {user.username}
                            </div>
                            <div
                              className="text-sm text-gray-500"
                              style={{ display: "none" }}
                            >
                              ID: {user.id}
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(
                          user.role
                        )}`}
                      >
                        {user.role === "admin_team" ? "Admin Team" : user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.role === "admin" ? (
                        <span className="text-red-600 font-medium">
                          All Permissions
                        </span>
                      ) : (
                        <span>{user.permissions?.length || 0} permissions</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(user)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Edit User"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        {!["connect@uns", "suresh"].includes(user.username) && (
                          <button
                            onClick={() => handleDelete(user.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete User"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Expanded Row */}
                  {expandedUsers.has(user.id) && (
                    <tr className="bg-gray-50">
                      <td colSpan="5" className="px-6 py-4">
                        <div className="space-y-4">
                          <h4 className="font-medium text-gray-900">
                            User Permissions
                          </h4>
                          {user.role === "admin" ? (
                            <p className="text-sm text-red-600">
                              This user has administrative privileges with
                              access to all system features.
                            </p>
                          ) : user.permissions?.length > 0 ? (
                            <div className="space-y-4">
                              {/* Regular Permissions */}
                              <div>
                                <h5 className="text-sm font-medium text-gray-700 mb-2">Feature Permissions</h5>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                  {user.permissions
                                    .filter(p => !p.startsWith('investors.column.'))
                                    .map((permission) => (
                                      <span
                                        key={permission}
                                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                      >
                                        {permission}
                                      </span>
                                    ))}
                                </div>
                              </div>
                              {/* Column Permissions */}
                              {user.permissions.some(p => p.startsWith('investors.column.')) && (
                                <div>
                                  <h5 className="text-sm font-medium text-gray-700 mb-2">📊 Investor Column Access</h5>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    {user.permissions
                                      .filter(p => p.startsWith('investors.column.'))
                                      .map((permission) => (
                                        <span
                                          key={permission}
                                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                                        >
                                          {permission.replace('investors.column.', '').replace(/_/g, ' ')}
                                        </span>
                                      ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">
                              No permissions assigned to this user.
                            </p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">
                  {editingUser ? "Edit User" : "Add New User"}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.username}
                    onChange={(e) =>
                      setFormData({ ...formData, username: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password {editingUser && "(leave blank to keep current)"}
                  </label>
                  <input
                    type="password"
                    required={!editingUser}
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="admin_team">Admin Team</option>
                    <option value="admin">Administrator</option>
                    <option value="investor">Investor</option>
                  </select>
                </div>

                {formData.role === "admin_team" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Permissions
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Choose which features this user can access. Check all that
                      apply.
                    </p>
                    <div className="space-y-4 max-h-96 overflow-y-auto border border-gray-200 rounded-md p-4 bg-gray-50">
                      {Object.entries(permissions).map(
                        ([category, categoryPermissions]) => (
                          <div key={category} className={category === "investors_columns" ? "border-t-2 border-blue-200 pt-4" : ""}>
                            <div className="flex items-center justify-between mb-2">
                              <h4 className={`font-semibold capitalize text-sm ${
                                category === "investors_columns" 
                                  ? "text-blue-700" 
                                  : "text-gray-900"
                              }`}>
                                {category === "inr"
                                  ? "INR"
                                  : category === "investors_columns"
                                  ? "📊 Investors Page - Column Access"
                                  : category.charAt(0).toUpperCase() +
                                    category.slice(1)}
                              </h4>
                              {category === "investors_columns" && (
                                <div className="flex space-x-2">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const columnPerms = categoryPermissions.map(p => p.name);
                                      setFormData(prev => ({
                                        ...prev,
                                        permissions: [...new Set([...prev.permissions, ...columnPerms])]
                                      }));
                                    }}
                                    className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                  >
                                    Select All Columns
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const columnPerms = categoryPermissions.map(p => p.name);
                                      setFormData(prev => ({
                                        ...prev,
                                        permissions: prev.permissions.filter(p => !columnPerms.includes(p))
                                      }));
                                    }}
                                    className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                                  >
                                    Clear Columns
                                  </button>
                                </div>
                              )}
                            </div>
                            {category === "investors_columns" && (
                              <p className="text-xs text-blue-600 mb-3 ml-3">
                                Control which columns this user can view and edit on the Investors page.
                              </p>
                            )}
                            <div className={`space-y-2 ml-3 ${
                              category === "investors_columns" 
                                ? "grid grid-cols-2 gap-2 space-y-0" 
                                : ""
                            }`}>
                              {categoryPermissions.map((permission) => (
                                <label
                                  key={permission.name}
                                  className={`flex items-start p-2 rounded cursor-pointer transition-colors ${
                                    category === "investors_columns"
                                      ? "hover:bg-blue-50 border border-gray-100"
                                      : "hover:bg-white"
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={formData.permissions.includes(
                                      permission.name
                                    )}
                                    onChange={() =>
                                      handlePermissionToggle(permission.name)
                                    }
                                    className={`mt-0.5 mr-3 h-4 w-4 focus:ring-blue-500 border-gray-300 rounded ${
                                      category === "investors_columns"
                                        ? "text-blue-600"
                                        : "text-blue-600"
                                    }`}
                                  />
                                  <span className="text-sm text-gray-700">
                                    {permission.description || permission.name}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {editingUser ? "Update User" : "Create User"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;
